import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage, humanBytes, savingsLabel } from "@/lib/imageCompress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  Pencil,
  Sparkles,
  Upload,
  Camera,
  RefreshCw,
  Package,
  X,
  Loader2,
  Info,
  AlertTriangle,
  History,
  Radio,
  FileDown,
} from "lucide-react";

const MAX_IMAGES = 5;

// Global disclaimer shown wherever product images are displayed to customers.
// Kept in one place so the wording stays consistent across the admin dialog,
// item card, and the future public storefront.
const IMAGE_DISCLAIMER =
  "Image for reference only. The actual product may differ in appearance, packaging, brand or minor specs.";

const IST_STAMP = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
      hour12: false,
    });
  } catch {
    return "—";
  }
};

/**
 * Parse either CSV or tab-separated (Excel paste) text into an array of item
 * objects. Header row required; columns matched case-insensitively.
 *
 * Recognised header names (any of):
 *   - name / item / stock / product   → name        (required)
 *   - qty / quantity                  → quantity
 *   - price / rate / amount           → price
 *   - unit                            → unit
 *   - group / category / brand        → group
 *   - description / notes             → description
 */
function parseBulkText(text) {
  const rows = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (rows.length < 2) return [];
  // Detect separator — tab if any header line contains a tab, else comma.
  const sep = rows[0].includes("\t") ? "\t" : ",";
  const cells = (line) => line.split(sep).map((c) => c.trim().replace(/^"|"$/g, ""));
  const headers = cells(rows[0]).map((h) => h.toLowerCase());
  const idxOf = (candidates) =>
    headers.findIndex((h) => candidates.some((c) => h === c || h.startsWith(c)));
  const nameIdx = idxOf(["name", "item", "stock", "product"]);
  const qtyIdx = idxOf(["qty", "quantity"]);
  const priceIdx = idxOf(["price", "rate", "amount"]);
  const unitIdx = idxOf(["unit"]);
  const groupIdx = idxOf(["group", "category", "brand"]);
  const descIdx = idxOf(["description", "notes"]);
  if (nameIdx < 0) return [];
  return rows.slice(1).map((line) => {
    const c = cells(line);
    return {
      name: c[nameIdx] || "",
      quantity: qtyIdx >= 0 ? Number(c[qtyIdx]) || 0 : 0,
      price: priceIdx >= 0 ? Number(c[priceIdx]) || 0 : 0,
      unit: unitIdx >= 0 ? c[unitIdx] || "Pcs." : "Pcs.",
      group: groupIdx >= 0 ? c[groupIdx] || "" : "",
      description: descIdx >= 0 ? c[descIdx] || "" : "",
    };
  }).filter((r) => r.name.trim());
}

export default function Marketplace() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [imgTarget, setImgTarget] = useState(null); // item currently having its images managed
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null); // { count, sample, errors }
  const [bulkBusy, setBulkBusy] = useState(false);
  // Live-sync history dialog — shows recent runs from the DialFlow Sync Agent
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    quantity: 0,
    price: 0,
    unit: "Pcs.",
    group: "",
    description: "",
    low_stock_threshold: 0,
  });

  const load = useCallback(async () => {
    const { data } = await api.get("/marketplace/items");
    setItems(data || []);
  }, []);

  // Pay-per-generated-image wallet — balance, the platform price, and the
  // super admin's QR code so the owner knows how much to pay and where.
  const [wallet, setWallet] = useState(null);
  const loadWallet = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet/mine");
      setWallet(data);
    } catch {
      /* non-fatal — the card just won't render */
    }
  }, []);

  const loadSyncHistory = useCallback(async () => {
    setSyncLoading(true);
    try {
      const { data } = await api.get("/marketplace/sync/history?limit=15");
      setSyncHistory(data?.items || []);
    } catch {
      // silent — the dialog itself will show the empty state
      setSyncHistory([]);
    } finally {
      setSyncLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadWallet();
  }, [load, loadWallet]);

  useEffect(() => {
    if (syncOpen) loadSyncHistory();
  }, [syncOpen, loadSyncHistory]);

  const lowStock = useMemo(
    () => items.filter((i) =>
      Number(i.low_stock_threshold || 0) > 0 &&
      Number(i.quantity || 0) <= Number(i.low_stock_threshold || 0)
    ),
    [items],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (i) =>
        (i.name || "").toLowerCase().includes(s) ||
        (i.group || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  // Group the (search-filtered) items by their `group` field so a 100+ item
  // marketplace is browsable. Ungrouped items land in "Other" which is
  // pinned to the end. Alphabetical otherwise.
  const groupedItems = useMemo(() => {
    const buckets = new Map();
    for (const it of filtered) {
      const g = (it.group || "").trim() || "Other";
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g).push(it);
    }
    return Array.from(buckets.entries()).sort(([a], [b]) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  // Every distinct group name already in use, for the New/Edit item dialog's
  // autocomplete — lets the owner pick an existing group instead of retyping
  // it (and accidentally forking it with a typo, e.g. "PRINTER ADN SCANNER"
  // vs "printer and scanner").
  const uniqueGroups = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      const g = (it.group || "").trim();
      if (g) set.add(g);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (g) => setCollapsedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  // Items still missing a product photo — surfaced as a banner so the shop
  // owner can auto-generate them in one go right after a Tally/Busy sync
  // dumps 100s of fresh rows with empty `images = []`.
  const noPhotoItems = useMemo(
    () => items.filter((i) => !i.images || i.images.length === 0),
    [items],
  );

  // Track which items are currently having their photo generated so the UI
  // can show a per-tile spinner. Stored as a Set of item IDs.
  const [autoImgBusy, setAutoImgBusy] = useState(() => new Set());
  const [bulkAutoImg, setBulkAutoImg] = useState(null); // { done, total } while running

  const markBusy = useCallback((id, on) => {
    setAutoImgBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const autoImage = useCallback(async (item) => {
    // One-tap: server generates + attaches a single photo in one round-trip.
    markBusy(item.id, true);
    try {
      const { data } = await api.post(`/marketplace/items/${item.id}/auto-image`);
      // Patch just this item locally so we don't refetch the whole grid.
      setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, images: [data.image] } : it));
      loadWallet();
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || err?.message || "Image generation failed";
      toast.error(`${item.name}: ${msg}`);
      // Bubble the failure reason up so the bulk summary can surface it too
      // (avoids the classic "all failed — retry later" mystery when the real
      // issue is e.g. GEMINI_API_KEY missing on the deploy target, or the
      // wallet balance running out).
      return { ok: false, status, msg };
    } finally {
      markBusy(item.id, false);
    }
  }, [markBusy, loadWallet]);

  const bulkAutoImage = useCallback(async () => {
    // Generate photos for every item missing one, with a small concurrency
    // cap so we don't hammer Gemini and trip its rate limits.
    const targets = items.filter((i) => !i.images || i.images.length === 0);
    if (targets.length === 0) return;
    const CONCURRENCY = 4;
    setBulkAutoImg({ done: 0, total: targets.length, failed: 0 });
    let cursor = 0;
    let ok = 0;
    let failed = 0;
    let firstError = null;
    const runNext = async () => {
      while (cursor < targets.length) {
        const idx = cursor++;
        const t = targets[idx];
        const res = await autoImage(t);
        if (res.ok) ok++; else { failed++; if (!firstError) firstError = res.msg; }
        setBulkAutoImg({ done: ok + failed, total: targets.length, failed });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, runNext));
    setBulkAutoImg(null);
    if (ok > 0) toast.success(`Generated ${ok} photo${ok === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
    else if (failed > 0) toast.error(`All ${failed} generations failed: ${firstError || "unknown error"}`, { duration: 10000 });
  }, [items, autoImage]);

  const resetForm = () =>
    setForm({ name: "", quantity: 0, price: 0, unit: "Pcs.", group: "", description: "", low_stock_threshold: 0 });

  const openCreate = () => { resetForm(); setCreating(true); };

  const submitCreate = async () => {
    if (!form.name.trim()) { toast.error("Item name required"); return; }
    try {
      await api.post("/marketplace/items", form);
      toast.success("Item added");
      setCreating(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add item");
    }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      quantity: t.quantity || 0,
      price: t.price || 0,
      unit: t.unit || "Pcs.",
      group: t.group || "",
      description: t.description || "",
      low_stock_threshold: t.low_stock_threshold || 0,
    });
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/marketplace/items/${editing.id}`, form);
      toast.success("Item updated");
      setEditing(null);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await api.delete(`/marketplace/items/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const submitBulk = async () => {
    const text = (bulkText || "").trim();
    if (!text) {
      toast.error("Paste at least one header row + one data row.");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/marketplace/items/bulk-csv", { text });
      if (!data.inserted) {
        toast.error(
          data.errors?.[0] ||
            "Could not parse any rows — check the header row (name / quantity / price / amount).",
        );
        return;
      }
      toast.success(`Imported ${data.inserted} of ${data.parsed} rows`);
      setBulkOpen(false);
      setBulkText("");
      setBulkPreview(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkPreview = async (text) => {
    if (!text || !text.trim()) {
      setBulkPreview(null);
      return;
    }
    try {
      const { data } = await api.post("/marketplace/items/bulk-preview", { text });
      setBulkPreview(data);
    } catch {
      setBulkPreview({ count: 0, sample: [], errors: ["Preview failed"] });
    }
  };

  const onBulkTextChange = (v) => {
    setBulkText(v);
    // Debounce preview so we don't hammer the server on every keystroke.
    clearTimeout(onBulkTextChange._t);
    onBulkTextChange._t = setTimeout(() => runBulkPreview(v), 350);
  };

  const onBulkFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setBulkText(text);
    runBulkPreview(text);
  };

  const downloadTemplate = () => {
    const csv = [
      "Item Name,Unit,Quantity,Amount,Group,Description",
      "MOTHER H110 FRONTECH,Pcs.,1,2300,MotherBoard,",
      "MotherBoard A520M-K Asus,Pcs.,2,3866.67,MotherBoard,",
      "MotherBoard B450 Asus Prime,Pcs.,3,10650,MotherBoard,",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "marketplace-bulk-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-marketplace">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Catalog</div>
          <h1 className="font-heading text-3xl font-bold mt-2 flex items-center gap-2">
            <Package className="w-7 h-7" /> Marketplace
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Manage your item catalog with AI-generated product photos. Up to {MAX_IMAGES} images per item.
          </p>
          <p className="text-amber-800 mt-1 text-xs flex items-center gap-1" data-testid="mp-header-disclaimer">
            <Info className="w-3.5 h-3.5" />
            {IMAGE_DISCLAIMER}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSyncOpen(true)}
            data-testid="mp-sync-btn"
            title="Live-sync stock from Tally / Busy"
          >
            <Radio className="w-4 h-4 mr-1" /> Live sync
          </Button>
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            data-testid="mp-bulk-btn"
          >
            <Upload className="w-4 h-4 mr-1" /> Bulk import
          </Button>
          <Button
            className="bg-[#0055FF] hover:bg-[#0040CC]"
            onClick={openCreate}
            data-testid="mp-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New item
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search by name or group…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="mp-search"
          className="max-w-md"
        />
      </div>

      <DownloadsSection />

      {lowStock.length > 0 && (
        <div
          className="mb-4 border border-red-200 bg-red-50 rounded-lg px-4 py-3 flex items-start gap-3"
          data-testid="mp-lowstock-banner"
        >
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium text-red-800">
              {lowStock.length} item{lowStock.length === 1 ? " is" : "s are"} running low
            </div>
            <div className="mono text-[11px] text-red-700 mt-0.5 line-clamp-2">
              {lowStock.slice(0, 4).map((i) => `${i.name} (${i.quantity} left)`).join(" · ")}
              {lowStock.length > 4 ? ` · +${lowStock.length - 4} more` : ""}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-red-300 text-red-700 hover:bg-red-100"
            onClick={() => setQ(lowStock[0].name)}
            data-testid="mp-lowstock-jump"
          >
            Review
          </Button>
        </div>
      )}

      {/* Wallet balance — only shown once the super admin has actually
          turned on pay-per-image billing (price_per_image > 0). Lets the
          owner see what they have left and how/where to top up. */}
      {wallet && wallet.price_per_image > 0 && (
        <div
          className="mb-4 border border-slate-200 bg-white rounded-lg px-4 py-3 flex items-center gap-4"
          data-testid="mp-wallet-card"
        >
          {wallet.qr_code && (
            <img
              src={wallet.qr_code}
              alt="Payment QR code"
              className="w-14 h-14 object-contain rounded border border-slate-200 shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800">
              Wallet balance: ₹{Number(wallet.balance).toFixed(2)}
            </div>
            <div className="mono text-[11px] text-slate-500 mt-0.5">
              ₹{Number(wallet.price_per_image).toFixed(2)} per generated photo — scan the QR to pay, then ask
              your admin to credit your wallet.
            </div>
          </div>
          {wallet.balance < wallet.price_per_image && (
            <span className="mono text-[10px] uppercase text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 shrink-0">
              Recharge needed
            </span>
          )}
        </div>
      )}

      {/* AI product-photo banner — appears whenever ≥1 item is missing a
          photo. Lets the owner one-tap generate photos for every empty
          tile at once (great right after a Tally / Busy sync dumps
          hundreds of rows with empty `images = []`). Runs 4-at-a-time so
          Gemini isn't hammered, shows live progress `done/total`. */}
      {noPhotoItems.length > 0 && (
        <div
          className="mb-4 border border-indigo-200 bg-gradient-to-r from-indigo-50 to-fuchsia-50 rounded-lg px-4 py-3 flex items-start gap-3"
          data-testid="mp-auto-photo-banner"
        >
          <Sparkles className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-indigo-900">
              {noPhotoItems.length} item{noPhotoItems.length === 1 ? " is" : "s are"} missing a product photo
            </div>
            <div className="mono text-[11px] text-indigo-700 mt-0.5 line-clamp-2">
              {bulkAutoImg
                ? `Generating… ${bulkAutoImg.done}/${bulkAutoImg.total}${bulkAutoImg.failed ? ` · ${bulkAutoImg.failed} failed` : ""}`
                : "One tap generates a studio-style catalog photo for every empty tile via Gemini Nano Banana."}
            </div>
          </div>
          <Button
            size="sm"
            onClick={bulkAutoImage}
            disabled={!!bulkAutoImg}
            className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
            data-testid="mp-auto-photo-all"
          >
            {bulkAutoImg ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {bulkAutoImg.done}/{bulkAutoImg.total}</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5 mr-1" /> Generate all photos</>
            )}
          </Button>
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <div className="mono text-sm text-slate-500">
              {items.length === 0
                ? "No items yet — add your first item or bulk import from Excel."
                : "No items match your search."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="mp-groups">
          {groupedItems.map(([groupName, groupList]) => {
            const collapsed = collapsedGroups.has(groupName);
            return (
              <section key={groupName} data-testid={`mp-group-${groupName}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(groupName)}
                  className="w-full flex items-center justify-between mb-3 group hover:opacity-90"
                  data-testid={`mp-group-toggle-${groupName}`}
                  aria-expanded={!collapsed}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg font-bold text-slate-900">{groupName}</span>
                    <span className="mono text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                      {groupList.length}
                    </span>
                  </div>
                  <span className="mono text-xs text-slate-400 group-hover:text-slate-700 transition">
                    {collapsed ? "Show +" : "Hide −"}
                  </span>
                </button>
                {!collapsed && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {groupList.map((t) => {
                      const isLow = Number(t.low_stock_threshold || 0) > 0 &&
                        Number(t.quantity || 0) <= Number(t.low_stock_threshold || 0);
                      return (
                      <Card
                        key={t.id}
              className={
                "border-slate-200 shadow-none overflow-hidden hoverable-card " +
                (isLow ? "ring-1 ring-red-300" : "")
              }
              data-testid={`mp-item-${t.id}`}
            >
              <div
                className="aspect-square bg-slate-100 relative cursor-pointer"
                onClick={() => setImgTarget(t)}
                data-testid={`mp-item-thumb-${t.id}`}
              >
                {t.images && t.images.length > 0 ? (
                  <img
                    src={t.images[0]}
                    alt={t.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                    {autoImgBusy.has(t.id) ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                        <div className="mono text-[10px] text-indigo-600">Generating…</div>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-6 h-6 mb-1" />
                        <div className="mono text-[10px]">Add images</div>
                        {/* One-tap AI photo pill — only visible on empty tiles.
                            Stops the click from bubbling to the parent (which
                            would open the full image dialog). */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); autoImage(t); }}
                          disabled={!!bulkAutoImg}
                          className="mono text-[10px] px-2 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1 disabled:opacity-50"
                          data-testid={`mp-auto-photo-${t.id}`}
                        >
                          <Sparkles className="w-3 h-3" /> Generate photo
                        </button>
                      </>
                    )}
                  </div>
                )}
                {t.images && t.images.length > 0 && (
                  <>
                    <div className="absolute bottom-1 right-1 mono text-[10px] bg-white/90 border border-slate-200 rounded px-1.5">
                      {t.images.length}/{MAX_IMAGES}
                    </div>
                    <div
                      className="absolute top-1 left-1 mono text-[9px] bg-amber-50/95 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5 flex items-center gap-1"
                      title={IMAGE_DISCLAIMER}
                      data-testid={`mp-item-disclaimer-${t.id}`}
                    >
                      <Info className="w-3 h-3" />
                      Reference
                    </div>
                  </>
                )}
                {isLow && (
                  <div
                    className="absolute top-1 right-1 mono text-[9px] bg-red-600 text-white rounded px-1.5 py-0.5 flex items-center gap-1"
                    title={`Below threshold of ${t.low_stock_threshold}`}
                    data-testid={`mp-item-lowstock-${t.id}`}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Low stock
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <div className="font-medium text-sm line-clamp-2" title={t.name}>
                  {t.name}
                </div>
                {t.group && (
                  <div className="mono text-[10px] text-slate-500 mt-0.5">{t.group}</div>
                )}
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="mono text-slate-600">
                    Qty: <b className={t.quantity > 0 ? (isLow ? "text-red-600" : "text-emerald-700") : "text-red-600"}>{t.quantity}</b> {t.unit}
                  </span>
                  <span className="mono text-slate-800 font-medium">₹{t.price?.toLocaleString("en-IN")}</span>
                </div>
                <div className="mono text-[9px] text-slate-400 mt-1">
                  by {t.created_by_name} · {IST_STAMP(t.created_at)}
                </div>
                <div className="flex gap-1 mt-2">
                  <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => openEdit(t)} data-testid={`mp-edit-${t.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => remove(t.id)} data-testid={`mp-delete-${t.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <ItemFormDialog
        open={creating || !!editing}
        title={editing ? "Edit item" : "New item"}
        form={form}
        setForm={setForm}
        onCancel={() => { setCreating(false); setEditing(null); resetForm(); }}
        onSubmit={editing ? submitEdit : submitCreate}
        groups={uniqueGroups}
      />

      {/* Bulk import dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) { setBulkPreview(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bulk import from Excel / CSV</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Upload a <span className="mono">.csv</span> file or paste rows from your spreadsheet.
              First row must be headers. Recognised columns (case-insensitive):
              <br />
              <span className="mono">Item Name, Unit, Quantity, Amount, Price, Group, Description</span>.
              If your sheet has <span className="mono">Amount</span> (total value)
              instead of unit price, we&apos;ll auto-divide by <span className="mono">Quantity</span>.
              Up to 500 rows per import.
              <br />
              <span className="inline-flex items-center gap-1 mt-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                <span className="mono text-[10px] uppercase tracking-wider">Works out-of-the-box with</span>
                <b>Tally Prime</b>
                <span className="text-emerald-500">·</span>
                <b>Busy Accounting</b>
              </span>
              <span className="block mt-1 text-[11px] text-slate-500">
                <b>Tally:</b> <span className="mono">Display More Reports → Stock Summary → Ctrl+E → CSV</span>.{" "}
                <b>Busy:</b> <span className="mono">Display → Item Master List → Export CSV</span>.
                Paste the file — no column renaming needed.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium" data-testid="mp-bulk-file-label">
              <Upload className="w-3.5 h-3.5" />
              Choose CSV file
              <input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                className="hidden"
                onChange={(e) => onBulkFile(e.target.files?.[0])}
                data-testid="mp-bulk-file"
              />
            </label>
            <button
              type="button"
              onClick={downloadTemplate}
              className="text-xs text-[#0055FF] hover:underline"
              data-testid="mp-bulk-template"
            >
              Download template
            </button>
            <span className="text-[11px] text-slate-400 ml-auto">
              Tip: in Excel, use <span className="mono">File → Save As → CSV</span> then upload here.
            </span>
          </div>

          <Textarea
            rows={10}
            className="mono text-xs mt-2"
            placeholder={`Item Name,Unit,Quantity,Amount\nMOTHER H110 FRONTECH,Pcs.,1,2300\nMotherBoard A520M-K Asus,Pcs.,2,3866.67`}
            value={bulkText}
            onChange={(e) => onBulkTextChange(e.target.value)}
            data-testid="mp-bulk-textarea"
          />

          {/* Live preview of what will actually be imported. */}
          {bulkPreview && (
            <div className="rounded-md border border-slate-200 bg-slate-50 max-h-56 overflow-auto" data-testid="mp-bulk-preview">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between text-xs">
                <span className="font-medium">
                  {bulkPreview.count} row{bulkPreview.count === 1 ? "" : "s"} ready to import
                </span>
                {bulkPreview.errors?.length ? (
                  <span className="text-red-600">{bulkPreview.errors[0]}</span>
                ) : bulkPreview.count > 0 ? (
                  <span className="text-emerald-700">Ready</span>
                ) : null}
              </div>
              {bulkPreview.sample?.length ? (
                <table className="w-full text-[11px]">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-1.5">Name</th>
                      <th className="text-left px-3 py-1.5">Qty</th>
                      <th className="text-left px-3 py-1.5">Unit price</th>
                      <th className="text-left px-3 py-1.5">Unit</th>
                      <th className="text-left px-3 py-1.5">Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkPreview.sample.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5">{r.name}</td>
                        <td className="px-3 py-1.5">{r.quantity}</td>
                        <td className="px-3 py-1.5">₹{Number(r.price).toFixed(2)}</td>
                        <td className="px-3 py-1.5">{r.unit}</td>
                        <td className="px-3 py-1.5">{r.group || "—"}</td>
                      </tr>
                    ))}
                    {bulkPreview.count > bulkPreview.sample.length && (
                      <tr className="border-t border-slate-100">
                        <td colSpan={5} className="px-3 py-1.5 text-slate-500 italic">
                          … and {bulkPreview.count - bulkPreview.sample.length} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : null}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              onClick={submitBulk}
              disabled={bulkBusy || !bulkPreview?.count}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="mp-bulk-submit"
            >
              {bulkBusy ? "Importing…" : bulkPreview?.count ? `Import ${bulkPreview.count} row${bulkPreview.count === 1 ? "" : "s"}` : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage images */}
      <ImagesDialog
        item={imgTarget}
        onClose={() => setImgTarget(null)}
        onSaved={async () => { await load(); }}
        onGenerated={loadWallet}
      />

      {/* Live sync history — surfaces every run of the DialFlow Sync Agent */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mp-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-emerald-600" /> Live sync from Tally / Busy
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              A tiny Python agent runs on your shop PC and pushes stock from
              Tally Prime or Busy into DialFlow every few minutes.
            </DialogDescription>
          </DialogHeader>

          {/* Quickstart — the 5 steps a shopkeeper needs */}
          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
              Quickstart · 5 minutes
            </div>
            <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
              <li>
                Install <b>Python 3.9+</b> on the shop PC —{" "}
                <a href="https://www.python.org/downloads/" target="_blank" rel="noreferrer noopener" className="text-emerald-700 hover:underline">python.org/downloads</a>{" "}
                (tick <span className="mono">Add Python to PATH</span> during install).
              </li>
              <li>
                <b>Download the starter kit</b> — a ~15 KB ZIP with everything
                needed (Python agent, config template, README, and two .bat
                launchers). Unzip into a folder like{" "}
                <span className="mono">C:\DialFlowSync\</span>.
                <div className="flex flex-wrap gap-2 mt-2">
                  <a
                    href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/agent/starter-zip`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-sm mono font-semibold"
                    data-testid="mp-sync-dl-zip"
                  >
                    <Package className="w-3.5 h-3.5" /> Download starter kit (.zip)
                  </a>
                  <details className="text-[11px] text-slate-500">
                    <summary className="cursor-pointer hover:text-slate-700">
                      Prefer individual files?
                    </summary>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <a
                        href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/agent/download`}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white text-[11px] mono"
                        data-testid="mp-sync-dl-script"
                      >
                        dialflow_sync.py
                      </a>
                      <a
                        href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/agent/config-template`}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white text-[11px] mono"
                        data-testid="mp-sync-dl-config"
                      >
                        dialflow_sync.ini
                      </a>
                    </div>
                  </details>
                </div>
              </li>
              <li>
                Open a Command Prompt in that folder and run this <u>one time</u>
                {" "}to install the Python HTTP library:
                <pre className="mono text-[11px] bg-slate-900 text-emerald-200 rounded p-2 mt-1 whitespace-pre-wrap">
{`pip install requests`}
                </pre>
              </li>
              <li>
                Edit <span className="mono">dialflow_sync.ini</span> in Notepad — set the shop URL,
                your DialFlow owner email &amp; password, and pick{" "}
                <span className="mono">mode = tally</span> or <span className="mono">busy</span>.
                Save the file.
              </li>
              <li>
                <b>Start the agent</b> — just double-click{" "}
                <span className="mono">run_dialflow_sync.bat</span>. A console
                window opens and shows the sync log live. Leave it running.
              </li>
              <li className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
                <b>Important — use your BACKEND URL, not your Vercel/Netlify URL.</b>{" "}
                Static hosts return <span className="mono">HTTP 405</span> for the login
                request. Set{" "}
                <span className="mono">url =</span> to something like{" "}
                <span className="mono">https://your-app.up.railway.app</span> (Railway) or{" "}
                <span className="mono">{(process.env.REACT_APP_BACKEND_URL || "https://<your-slug>.preview.emergentagent.com")}</span>{" "}
                (Emergent preview). Sanity check: open{" "}
                <span className="mono">&lt;url&gt;/api/</span> in a browser — you should see JSON,
                not the storefront.
              </li>
              <li>
                <b>Auto-start on boot (optional)</b> — right-click{" "}
                <span className="mono">install_startup.bat</span> →{" "}
                <span className="mono">Run as administrator</span>. It registers
                the agent as a Windows Scheduled Task so it starts automatically
                every time the shop PC logs in. Only run this once. Full guide:{" "}
                <a
                  href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/agent/readme`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-emerald-700 hover:underline"
                  data-testid="mp-sync-readme"
                >
                  Read the full guide →
                </a>
              </li>
            </ol>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600 mt-3">
            <div className="flex items-center gap-1.5 mono text-slate-800 mb-1">
              <History className="w-3.5 h-3.5" /> Recent runs
            </div>
            <div>
              Any log entry here proves the agent reached the backend. If this
              is empty, either the agent hasn&apos;t run yet, or its login
              credentials are wrong. Check <span className="mono">dialflow_sync.log</span> on the shop PC.
            </div>
          </div>

          <div
            className="rounded-md border border-slate-200 max-h-56 overflow-auto mt-2"
            data-testid="mp-sync-history"
          >
            {syncLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : syncHistory.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                No syncs yet. Once the agent runs on your shop PC, entries appear here.
              </div>
            ) : (
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5">When</th>
                    <th className="text-left px-3 py-1.5">Source</th>
                    <th className="text-right px-3 py-1.5">New</th>
                    <th className="text-right px-3 py-1.5">Updated</th>
                    <th className="text-right px-3 py-1.5">Unchanged</th>
                    <th className="text-right px-3 py-1.5">Zeroed</th>
                    <th className="text-left px-3 py-1.5">Full</th>
                  </tr>
                </thead>
                <tbody>
                  {syncHistory.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 mono text-[11px] text-slate-600">
                        {IST_STAMP(r.at)}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="mono text-[10px] uppercase tracking-wider bg-slate-100 rounded px-1.5 py-0.5">
                          {r.source}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-emerald-700 font-medium">+{r.inserted || 0}</td>
                      <td className="px-3 py-1.5 text-right text-blue-700">~{r.updated || 0}</td>
                      <td className="px-3 py-1.5 text-right text-slate-500">={r.unchanged || 0}</td>
                      <td className="px-3 py-1.5 text-right text-red-600">×{r.zeroed || 0}</td>
                      <td className="px-3 py-1.5 text-[11px] text-slate-500">
                        {r.full_sync ? "yes" : "no"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={loadSyncHistory}
              disabled={syncLoading}
              data-testid="mp-sync-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${syncLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              type="button"
              onClick={() => setSyncOpen(false)}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DownloadsSection() {
  const [downloads, setDownloads] = useState([]);
  const [open, setOpen] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", description: "", file: null });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/downloads");
      setDownloads(data || []);
    } catch {
      /* non-fatal — the section just stays empty */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitUpload = async () => {
    if (!uploadForm.title.trim()) return toast.error("Title is required");
    if (!uploadForm.file) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", uploadForm.title.trim());
      fd.append("description", uploadForm.description || "");
      fd.append("file", uploadForm.file);
      await api.post("/downloads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File added");
      setUploadOpen(false);
      setUploadForm({ title: "", description: "", file: null });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file permanently?")) return;
    try {
      await api.delete(`/downloads/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <Card className="border-slate-200 shadow-none mb-4" data-testid="downloads-section">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3"
          data-testid="downloads-toggle"
        >
          <div className="flex items-center gap-2">
            <FileDown className="w-4 h-4 text-slate-500" />
            <span className="font-heading text-sm font-bold">Downloads for customers</span>
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded px-2 py-0.5">
              {downloads.length}
            </span>
          </div>
          <span className="mono text-xs text-slate-400">{open ? "Hide −" : "Show +"}</span>
        </button>
        {open && (
          <div className="px-4 pb-4">
            <p className="text-xs text-slate-500 mb-3">
              Software, drivers, manuals — any file customers can download from your shop page.
            </p>
            <div className="border border-slate-200 rounded-md divide-y mb-3">
              {downloads.map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`download-row-${d.id}`}>
                  <FileDown className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    <div className="mono text-[10px] text-slate-400 truncate">
                      {d.file_name} · {humanBytes(d.file_size)}
                      {d.download_count ? ` · ${d.download_count} download${d.download_count === 1 ? "" : "s"}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-red-600 hover:text-red-700"
                    onClick={() => remove(d.id)}
                    data-testid={`download-delete-${d.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {downloads.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">No files yet.</p>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)} data-testid="download-add">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add file
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a downloadable file</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Any file type — software installers, drivers, manuals. Max 300 MB.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Epson L3310 printer driver"
                autoFocus
                data-testid="download-form-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                rows={2}
                value={uploadForm.description}
                onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="download-form-desc"
              />
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                data-testid="download-form-file"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button
              onClick={submitUpload}
              disabled={uploading}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="download-form-submit"
            >
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ItemFormDialog({ open, title, form, setForm, onCancel, onSubmit, groups }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Set the item name, stock, price and (optionally) a low-stock alert threshold.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Item name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="mp-form-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="mp-form-qty"
              />
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="Pcs."
                data-testid="mp-form-unit"
              />
            </div>
            <div className="space-y-2">
              <Label>Price (₹)</Label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="mp-form-price"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Group / Category</Label>
            <Input
              value={form.group}
              onChange={(e) => setForm({ ...form, group: e.target.value })}
              placeholder="e.g. MotherBoard"
              list="mp-form-group-options"
              data-testid="mp-form-group"
            />
            <datalist id="mp-form-group-options">
              {(groups || []).map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label>
              Low-stock alert threshold{" "}
              <span className="text-slate-400 text-xs">(optional — 0 to disable)</span>
            </Label>
            <Input
              type="number"
              min="0"
              value={form.low_stock_threshold}
              onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
              placeholder="e.g. 5"
              data-testid="mp-form-lowstock"
            />
            <p className="text-[10px] text-slate-500">
              Owner gets a red badge on this item when quantity drops to this level or below.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="mp-form-desc"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="mp-form-submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImagesDialog({ item, onClose, onSaved, onGenerated }) {
  const [images, setImages] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setImages(item.images || []);
      setCandidates([]);
    }
  }, [item]);

  if (!item) return null;

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/marketplace/generate-images", {
        query: item.name,
        count: MAX_IMAGES,
      });
      setCandidates(data.images || []);
      toast.success(`Generated ${data.count} candidate images`);
      onGenerated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate images");
    } finally {
      setGenerating(false);
    }
  };

  const acceptCandidate = (src) => {
    if (images.length >= MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images. Remove one first.`);
      return;
    }
    setImages([...images, src]);
    setCandidates(candidates.filter((c) => c !== src));
  };

  const removeImage = (idx) => setImages(images.filter((_, i) => i !== idx));

  const uploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input immediately so re-uploading the same file re-fires onChange.
    e.target.value = "";
    let totalOriginal = 0;
    let totalCompressed = 0;
    let added = 0;
    for (const f of files) {
      if (images.length + added >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images. Remove one first.`);
        break;
      }
      try {
        const r = await compressImage(f);
        totalOriginal += r.originalBytes;
        totalCompressed += r.compressedBytes;
        added += 1;
        setImages((prev) =>
          prev.length < MAX_IMAGES ? [...prev, r.dataUrl] : prev,
        );
      } catch (err) {
        toast.error(`Could not read ${f.name}: ${err.message || err}`);
      }
    }
    if (added > 0) {
      const saving = savingsLabel(totalOriginal, totalCompressed);
      toast.success(
        saving
          ? `${added} photo${added === 1 ? "" : "s"} optimised — ${humanBytes(totalOriginal)} → ${humanBytes(totalCompressed)} (${saving})`
          : `${added} photo${added === 1 ? "" : "s"} added`,
      );
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/marketplace/items/${item.id}/images`, { images });
      toast.success("Images saved");
      await onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#0055FF]" />
            Images for &ldquo;{item.name}&rdquo;
          </DialogTitle>
        </DialogHeader>

        {/* Product-image disclaimer — repeated everywhere images are shown so
            the customer expectation is set at every touchpoint. */}
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="mp-image-disclaimer"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Reference image:</b> {IMAGE_DISCLAIMER} Customers will see this
            same warning on every product image in the storefront.
          </div>
        </div>

        {/* Current images grid */}
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">
            Selected · {images.length}/{MAX_IMAGES}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {images.map((src, i) => (
              <div key={i} className="relative aspect-square border border-slate-200 rounded overflow-hidden group">
                <img src={src} alt={`img${i}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`mp-img-remove-${i}`}
                  title="Remove"
                >
                  <X className="w-4 h-4 text-red-600" />
                </button>
              </div>
            ))}
            {images.length < MAX_IMAGES &&
              Array.from({ length: MAX_IMAGES - images.length }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="aspect-square border-2 border-dashed border-slate-200 rounded flex items-center justify-center text-slate-300 mono text-[10px]"
                >
                  empty
                </div>
              ))}
          </div>
        </div>

        {/* Actions row */}
        <div className="flex flex-wrap gap-2 mt-2">
          <Button
            onClick={generate}
            disabled={generating}
            className="bg-[#0055FF] hover:bg-[#0040CC]"
            data-testid="mp-generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {generating ? "Generating…" : candidates.length ? "Regenerate" : "AI: generate 5 images"}
          </Button>
          <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50 text-sm" data-testid="mp-img-upload-label">
            <Upload className="w-4 h-4" /> Upload
            <input type="file" accept="image/*" multiple className="hidden" onChange={uploadFile} data-testid="mp-img-upload" />
          </label>
          <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50 text-sm" data-testid="mp-img-camera-label">
            <Camera className="w-4 h-4" /> Take photo
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadFile} data-testid="mp-img-camera" />
          </label>
          {candidates.length > 0 && (
            <Button variant="outline" onClick={() => setCandidates([])}>
              <RefreshCw className="w-4 h-4 mr-1" /> Clear candidates
            </Button>
          )}
        </div>

        {/* Candidate grid */}
        {candidates.length > 0 && (
          <div className="mt-3">
            <div className="mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">
              AI candidates · click to add
            </div>
            <div className="grid grid-cols-5 gap-2">
              {candidates.map((src, i) => (
                <button
                  key={i}
                  onClick={() => acceptCandidate(src)}
                  className="aspect-square border border-slate-200 rounded overflow-hidden hover:border-[#0055FF] hover:ring-2 hover:ring-blue-100 transition"
                  data-testid={`mp-candidate-${i}`}
                >
                  <img src={src} alt={`candidate ${i}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mp-images-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save {images.length} image{images.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
